import React, { useState, useEffect } from 'react';
import { Box, Button, Dialog, DialogContent, DialogTitle, Typography, IconButton, Divider, TextField, Radio, Tooltip } from '@mui/material';
import { Close, KeyboardArrowLeft, KeyboardArrowRight, Add } from '@mui/icons-material';
import { headingBlue, red, green, orange, lightGrey, mediumGrey, pureWhite, maroon } from '../../assets/colors';
import CustomTextField from '../CustomTextField';
import CustomDropDown from '../CustomDropDown';
import Loader from '../Loader';
import HttpService from '../../backend/requestApis';
import { fetchCourseQuestions, createCourseQuestion } from '../../backend/backendApis';
import { useDispatch } from 'react-redux';
import { showSnackBar } from '../CustomComponents/CustomComponentsSlice';

const getDifficultyBadge = (difficulty) => {
    const diff = (difficulty || '').toLowerCase();
    let bg = '#eee';
    let fg = '#666';
    if (diff === 'easy') {
        bg = '#e8f5e9';
        fg = '#2e7d32';
    } else if (diff === 'medium') {
        bg = '#fff3e0';
        fg = '#ef6c00';
    } else if (diff === 'hard') {
        bg = '#ffebee';
        fg = '#c62828';
    }

    return (
        <span style={{
            fontSize: '0.7rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            padding: '2px 8px',
            borderRadius: '4px',
            backgroundColor: bg,
            color: fg,
            marginLeft: '8px',
            verticalAlign: 'middle'
        }}>
            {diff}
        </span>
    );
};

const CourseQuestionsDialog = ({ dialogStatus, updateFunc, courseId, courseName }) => {
    const dispatch = useDispatch();
    const [questions, setQuestions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    
    // Pagination states
    const [page, setPage] = useState(1);
    const [offset, setOffset] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);

    // Question form state
    const [isAddingQuestion, setIsAddingQuestion] = useState(false);
    const [newQuestionText, setNewQuestionText] = useState('');
    const [newQuestionDifficulty, setNewQuestionDifficulty] = useState('Easy');
    const [newQuestionOptions, setNewQuestionOptions] = useState(['', '', '', '']);
    const [newQuestionCorrectIndex, setNewQuestionCorrectIndex] = useState(0);
    const [isSavingQuestion, setIsSavingQuestion] = useState(false);

    // Duplicate check states
    const [duplicateWarningDialog, setDuplicateWarningDialog] = useState(false);
    const [duplicateQuestionData, setDuplicateQuestionData] = useState(null);

    // Form errors
    const [newQuestionTextError, setNewQuestionTextError] = useState('');
    const [newQuestionOptionsError, setNewQuestionOptionsError] = useState('');

    // Debounce the search input
    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            setSearchTerm(searchQuery);
        }, 400);
        return () => clearTimeout(delayDebounce);
    }, [searchQuery]);

    // Reset page to 1 when search term changes
    useEffect(() => {
        setPage(1);
    }, [searchTerm]);

    const fetchQuestionsList = () => {
        if (!courseId) return;
        setIsLoading(true);
        const options = {
            url: fetchCourseQuestions,
            method: 'GET',
            params: { 
                courseId: courseId,
                search: searchTerm && searchTerm.trim() ? searchTerm.trim() : undefined,
                page: page,
                offset: offset
            },
            headers: {
                Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : null
            }
        };

        HttpService(options).then((resp) => {
            const responseData = resp?.data?.data || resp?.data || {};
            const qList = responseData.data || [];
            setQuestions(Array.isArray(qList) ? qList : []);
            setTotalPages(responseData.totalPages || 1);
            setTotalRecords(responseData.totalRecords || 0);
            setIsLoading(false);
        }).catch((err) => {
            console.error("Error fetching course questions: ", err);
            setIsLoading(false);
        });
    };

    useEffect(() => {
        if (dialogStatus && courseId) {
            fetchQuestionsList();
        }
    }, [dialogStatus, courseId, searchTerm, page, offset]);

    // Reset state on open/close
    useEffect(() => {
        if (dialogStatus) {
            setSearchQuery('');
            setSearchTerm('');
            setQuestions([]);
            setPage(1);
            setOffset(10);
            setTotalPages(1);
            setTotalRecords(0);
            setIsAddingQuestion(false);
            resetForm();
        }
    }, [dialogStatus]);

    const resetForm = () => {
        setNewQuestionText('');
        setNewQuestionDifficulty('Easy');
        setNewQuestionOptions(['', '', '', '']);
        setNewQuestionCorrectIndex(0);
        setNewQuestionTextError('');
        setNewQuestionOptionsError('');
        setIsSavingQuestion(false);
        setDuplicateWarningDialog(false);
        setDuplicateQuestionData(null);
    };

    const handleOptionChange = (text, index) => {
        const updatedOptions = [...newQuestionOptions];
        updatedOptions[index] = text;
        setNewQuestionOptions(updatedOptions);
    };

    const handleSaveQuestion = (forceSave = false) => {
        let isValid = true;

        if (!newQuestionText || !String(newQuestionText).trim()) {
            setNewQuestionTextError("Question text is required");
            isValid = false;
        } else {
            setNewQuestionTextError("");
        }

        if (!Array.isArray(newQuestionOptions) || newQuestionOptions.some(opt => !opt || !String(opt).trim())) {
            setNewQuestionOptionsError("All options must be filled");
            isValid = false;
        } else {
            setNewQuestionOptionsError("");
        }
        
        if (!isValid) return;

        setIsSavingQuestion(true);
        const options = {
            url: createCourseQuestion,
            method: 'POST',
            payload: {
                courseId,
                questionText: newQuestionText,
                difficulty: newQuestionDifficulty.toLowerCase(),
                options: newQuestionOptions,
                correctAnswer: newQuestionOptions[newQuestionCorrectIndex],
                forceSave: forceSave === true
            },
            headers: { 
                headers: { 
                    Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : null 
                } 
            }
        };

        HttpService(options)
            .then(() => {
                dispatch(showSnackBar({ message: 'Question added successfully!', severity: 'success' }));
                setIsAddingQuestion(false);
                resetForm();
                if (page === 1) {
                    fetchQuestionsList();
                } else {
                    setPage(1);
                }
            })
            .catch((err) => {
                const status = err?.response?.status || err?.status;
                const errorResponse = err?.response?.data || err?.data || err || {};
                const data = errorResponse?.data || errorResponse;

                if (status === 406) {
                    const similarity = data?.similarity || errorResponse?.similarity || 0;
                    const duplicateQuestion = data?.duplicateQuestion;

                    setDuplicateQuestionData({
                        similarity: similarity,
                        similarQuestion: duplicateQuestion?.questionText,
                        options: Array.isArray(duplicateQuestion?.options) ? duplicateQuestion.options : null,
                        correctAnswer: duplicateQuestion?.correctAnswer,
                        difficulty: duplicateQuestion?.difficulty
                    });
                    setDuplicateWarningDialog(true);
                    setIsSavingQuestion(false);
                    return;
                }

                console.error("Error adding question: ", err);
                dispatch(showSnackBar({ message: 'Error adding question. Please try again.', severity: 'error' }));
                setIsSavingQuestion(false);
            });
    };

    const titleStyle = {
        backgroundColor: headingBlue, 
        color: 'white',
        fontSize: '1rem',
        display: "flex",
        alignItems: 'center',
        padding: "0.5rem 1.25rem",
        justifyContent: 'space-between'
    };

    return (
        <>
            <Dialog maxWidth='md' fullWidth open={dialogStatus} onClose={() => updateFunc(false)}>
                <DialogTitle style={titleStyle}>
                    <Typography style={{ fontWeight: 600 }}>Questions Bank: {courseName || '-'}</Typography>
                    <IconButton onClick={() => updateFunc(false)} style={{ color: 'white', padding: 0 }}>
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent
                    sx={{
                        padding: '1.5rem !important',
                        backgroundColor: '#fdfdfd',
                        display: 'flex',
                        flexDirection: 'column',
                        maxHeight: '80vh'
                    }}
                >
                    {isLoading && <Loader />}
                    
                    {/* Header: Search and Toggle Add question */}
                    <Box style={{ marginBottom: '1rem', display: 'flex', gap: '1rem', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ flex: 1 }}>
                            <CustomTextField 
                                placeHolder="Enter Text to Search" 
                                value={searchQuery} 
                                setFunc={setSearchQuery}
                                width="100%"
                            />
                        </Box>
                        {!isAddingQuestion && (
                            <Button 
                                variant="contained" 
                                startIcon={<Add />}
                                style={{ backgroundColor: headingBlue, color: 'white', padding: '0.3rem 1.25rem', textTransform: 'capitalize' }} 
                                onClick={() => setIsAddingQuestion(true)}
                            >
                                Add Question
                            </Button>
                        )}
                    </Box>

                    {/* Collapsible Add Question Form */}
                    {isAddingQuestion && (
                        <Box style={{ padding: '1.2rem', border: `1.5px solid ${headingBlue}`, borderRadius: '8px', marginBottom: '1.2rem', backgroundColor: '#f4f9ff', display: 'flex', flexDirection: 'column', gap: '1rem', position: 'relative' }}>
                            {isSavingQuestion && <Loader />}
                            <Typography style={{ fontWeight: 600, color: headingBlue, fontSize: '0.95rem' }}>Create New Question</Typography>
                            
                            <Box style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                                <Box style={{ width: '100%' }}>
                                    <TextField 
                                        fullWidth 
                                        size="small" 
                                        label="Question Text *" 
                                        variant="outlined" 
                                        placeholder="Enter the question here..."
                                        sx={{ backgroundColor: 'white' }}
                                        value={newQuestionText}
                                        onChange={(e) => { setNewQuestionText(e.target.value); setNewQuestionTextError(""); }}
                                    />
                                    <Typography style={{ color: red, fontSize: '0.75rem', marginTop: '0.2rem', marginLeft: '0.4rem', fontWeight: 500 }}>{newQuestionTextError}</Typography>
                                </Box>
                                <Box style={{ width: '12rem' }}>
                                    <CustomDropDown
                                        placeHolder="Difficulty *"
                                        list={[{name: 'Easy'}, {name: 'Medium'}, {name: 'Hard'}]}
                                        value={newQuestionDifficulty}
                                        setFunc={(val) => setNewQuestionDifficulty(typeof val === 'object' ? val.name : val)}
                                        width="100%"
                                        regular={true}
                                    />
                                </Box>
                            </Box>

                            <Box style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', backgroundColor: 'white', padding: '0.8rem', borderRadius: '4px', border: `1px solid ${mediumGrey}` }}>
                                <Typography style={{ fontSize: '0.8rem', color: headingBlue, fontWeight: 600, marginBottom: '0.2rem' }}>Options (Select the correct answer)</Typography>
                                {[0, 1, 2, 3].map((idx) => (
                                    <Box key={idx} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                        <Radio 
                                            checked={newQuestionCorrectIndex === idx} 
                                            onChange={() => setNewQuestionCorrectIndex(idx)}
                                            size="small" 
                                            style={{ color: newQuestionCorrectIndex === idx ? green : mediumGrey, padding: '0.2rem' }} 
                                        />
                                        <TextField 
                                            fullWidth 
                                            size="small" 
                                            placeholder={`Option ${idx + 1}`} 
                                            variant="outlined" 
                                            value={newQuestionOptions[idx]}
                                            onChange={(e) => { handleOptionChange(e.target.value, idx); setNewQuestionOptionsError(""); }}
                                        />
                                    </Box>
                                ))}
                                <Typography style={{ color: red, fontSize: '0.75rem', marginTop: '0.1rem', marginLeft: '0.4rem', fontWeight: 500 }}>{newQuestionOptionsError}</Typography>
                            </Box>

                            <Box style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                                <Button size="small" variant="outlined" style={{ color: red, borderColor: red, textTransform: 'capitalize' }} onClick={resetForm}>
                                    Cancel
                                </Button>
                                <Button size="small" variant="contained" style={{ backgroundColor: headingBlue, color: 'white', textTransform: 'capitalize' }} onClick={() => handleSaveQuestion(false)} disabled={isSavingQuestion}>
                                    Save Question
                                </Button>
                            </Box>
                        </Box>
                    )}

                    <Box sx={{ flex: 1, overflowY: 'auto', pr: '4px', "&::-webkit-scrollbar": { display: 'none' }, scrollbarWidth: "none" }}>
                        {(questions.length === 0 && !isLoading) ? (
                            <Typography style={{ textAlign: 'center', color: '#888', padding: '2rem' }}>
                                No questions found for this course. Click "Add Question" to create one.
                            </Typography>
                        ) : (
                            questions.map((q, idx) => {
                                const displayIdx = ((page - 1) * offset) + idx + 1;
                                return (
                                    <Box 
                                        key={q._id || idx} 
                                        style={{ 
                                            padding: '1rem', 
                                            border: `1px solid ${mediumGrey}`, 
                                            borderRadius: '8px', 
                                            marginBottom: '1rem', 
                                            backgroundColor: 'white',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
                                        }}
                                    >
                                        <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                            <Typography style={{ fontWeight: 600, color: headingBlue, fontSize: '0.95rem' }}>
                                                {displayIdx}. {q.questionText || 'Unknown Question'}
                                            </Typography>
                                            {getDifficultyBadge(q.difficulty)}
                                        </Box>

                                        <Box style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '1rem', marginLeft: '0.5rem' }}>
                                            {Array.isArray(q.options) && q.options.map((opt, oIdx) => {
                                                const isCorrect = String(opt).trim().toLowerCase() === String(q.correctAnswer).trim().toLowerCase();
                                                return (
                                                    <Box 
                                                        key={oIdx} 
                                                        style={{ 
                                                            padding: '0.4rem 0.8rem', 
                                                            border: `1px solid ${isCorrect ? green : mediumGrey}`, 
                                                            borderRadius: '4px', 
                                                            backgroundColor: isCorrect ? '#e8f5e9' : '#fafafa', 
                                                            display: 'flex', 
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center'
                                                        }}
                                                    >
                                                        <Typography style={{ fontSize: '0.85rem', color: isCorrect ? 'green' : 'black', fontWeight: isCorrect ? 600 : 400 }}>
                                                            {opt}
                                                        </Typography>
                                                        {isCorrect && (
                                                            <Typography style={{ fontSize: '0.75rem', color: 'green', fontWeight: 600 }}>
                                                                Correct Answer
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                        <Divider style={{ margin: '1rem 0 0.5rem 0' }} />
                                        <Box style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                            <Typography style={{ fontSize: '0.75rem', color: '#888' }}>
                                                Added By: {q.addedByName || '-'} {q.addedByCN ? `(${q.addedByCN})` : ''}
                                            </Typography>
                                        </Box>
                                    </Box>
                                );
                            })
                        )}
                    </Box>

                    {/* Pagination Box */}
                    {!isLoading && questions.length > 0 && (
                        <Box style={{ display: 'flex', justifyContent: 'center', padding: "0.5rem 0rem", borderTop: `1px solid ${mediumGrey}`, marginTop: '1rem', alignItems: 'center' }}>
                            <Box style={{ width: "30%", display: 'flex', justifyContent: 'flex-start', alignItems: 'center', padding: "0rem 0.5rem 0rem 0.9rem" }}>
                                <Typography style={{ color: maroon, fontSize: '0.9rem' }}>
                                    {((page - 1) * offset) + 1} - {Math.min(page * offset, totalRecords)} of {totalRecords}
                                </Typography>
                            </Box>
                            <Box style={{ width: '40%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <IconButton 
                                    size='small' 
                                    onClick={() => { if(page > 1) setPage(page - 1) }} 
                                    disabled={page === 1}
                                >
                                    <KeyboardArrowLeft size='small' sx={{ color: page === 1 ? mediumGrey : headingBlue }} />
                                </IconButton>
                                
                                <Button variant='contained' style={{ minWidth: '0rem', width: '1.5rem', height: '1.5rem', borderRadius: '50%', backgroundColor: headingBlue, color: 'white', padding: '0rem', alignItems: 'center', fontSize: '0.85rem' }}>
                                    {page}
                                </Button>
                                
                                <IconButton 
                                    size='small' 
                                    onClick={() => { if(page < totalPages) setPage(page + 1) }} 
                                    disabled={page === totalPages || !totalPages}
                                >
                                    <KeyboardArrowRight size='small' sx={{ color: (page === totalPages || !totalPages) ? mediumGrey : headingBlue }} />
                                </IconButton>
                            </Box>
                            <Box style={{ width: '30%', display: 'flex', justifyContent: 'flex-end', padding: "0rem 0.6rem 0rem 0rem" }}>
                                <CustomDropDown 
                                    key='table' 
                                    value={offset} 
                                    width="5rem" 
                                    setFunc={(val) => { 
                                        setOffset(typeof val === 'object' ? val.name : val); 
                                        setPage(1); 
                                    }} 
                                    list={[{name: 10}, {name: 20}, {name: 50}]} 
                                    regular={true} 
                                />
                            </Box>
                        </Box>
                    )}

                    <Box style={{ padding: '1rem 0rem 0rem 0rem', display: 'flex', justifyContent: 'flex-end', borderTop: `1px solid ${mediumGrey}`, marginTop: '1rem' }}>
                        <Button 
                            variant="contained"
                            style={{ backgroundColor: headingBlue, color: 'white', padding: '0.3rem 2rem', textTransform: 'capitalize' }} 
                            onClick={() => updateFunc(false)}
                        >
                            Close
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Similarity Duplicity Dialog */}
            <Dialog maxWidth="sm" fullWidth open={duplicateWarningDialog} onClose={() => setDuplicateWarningDialog(false)}>
                <DialogTitle style={{ backgroundColor: headingBlue, color: 'white', fontSize: '1rem', padding: '0.4rem 1rem' }}>
                    Duplication Warning
                </DialogTitle>
                <DialogContent style={{ marginTop: '1rem', textAlign: 'center' }}>
                    {isSavingQuestion && <Loader />}
                    <Typography style={{ color: '#333', marginBottom: '1rem', fontSize: '0.9rem' }}>
                        A similar question already exists in this course assessment.
                    </Typography>
                    {duplicateQuestionData && (
                        <Box style={{ padding: '0.8rem', backgroundColor: '#f5f5f5', borderRadius: '4px', marginBottom: '1rem', textAlign: 'left' }}>
                            <Typography style={{ fontSize: '0.82rem', color: '#666' }}>Similar Question ({Math.round(duplicateQuestionData.similarity * 100)}% match):</Typography>
                            <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginTop: '0.2rem' }}>
                                <Typography style={{ fontSize: '0.9rem', fontWeight: 600, color: headingBlue }}>{duplicateQuestionData.similarQuestion}</Typography>
                                {duplicateQuestionData.difficulty && (
                                    <Typography style={{ fontSize: '0.7rem', fontWeight: 600, padding: '0.1rem 0.4rem', borderRadius: '4px', backgroundColor: duplicateQuestionData.difficulty?.toLowerCase() === 'hard' ? '#ffebee' : duplicateQuestionData.difficulty?.toLowerCase() === 'medium' ? '#fff3e0' : '#e8f5e9', color: duplicateQuestionData.difficulty?.toLowerCase() === 'hard' ? red : duplicateQuestionData.difficulty?.toLowerCase() === 'medium' ? orange : green, textTransform: 'capitalize' }}>
                                        {duplicateQuestionData.difficulty}
                                    </Typography>
                                )}
                            </Box>
                            {duplicateQuestionData.options && Array.isArray(duplicateQuestionData.options) && (
                                <Box style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', marginTop: '0.5rem', marginLeft: '0.5rem' }}>
                                    {duplicateQuestionData.options.map((opt, idx) => {
                                        const optText = typeof opt === 'object' ? (opt.text || opt.optionText || opt.option) : opt;
                                        const isCorrect = (duplicateQuestionData.correctAnswer === optText) || opt?.isCorrect;
                                        return (
                                            <Typography key={idx} style={{ fontSize: '0.8rem', color: isCorrect ? green : '#555', fontWeight: isCorrect ? 600 : 400 }}>
                                                {idx + 1}. {optText} {isCorrect ? '(Correct)' : ''}
                                            </Typography>
                                        );
                                    })}
                                </Box>
                            )}
                        </Box>
                    )}
                    <Typography style={{ color: '#333', fontSize: '0.9rem', marginBottom: '1rem' }}>
                        {duplicateQuestionData?.similarity >= 0.9 
                            ? "This question is too similar to an existing one and cannot be saved."
                            : "Do you still want to save this question?"}
                    </Typography>
                    <Box style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginTop: '1rem' }}>
                        <Button size="small" style={{ backgroundColor: red, color: 'white', padding: '0.2rem 1rem', textTransform: 'capitalize' }} onClick={() => setDuplicateWarningDialog(false)}>
                            {duplicateQuestionData?.similarity >= 0.9 ? "Close" : "Cancel"}
                        </Button>
                        {(!duplicateQuestionData || duplicateQuestionData.similarity < 0.9) && (
                            <Button size="small" style={{ backgroundColor: headingBlue, color: 'white', padding: '0.2rem 1rem', textTransform: 'capitalize' }} onClick={() => handleSaveQuestion(true)} disabled={isSavingQuestion}>
                                Save Anyway
                              </Button>
                        )}
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default CourseQuestionsDialog;
